import type { ISelectOption } from "../app-utils/form.js";
import type { IPlaybookPage } from "../app-utils/playbook.js";
import type { Dictionary } from "../app-utils/type.js";
import type {
  AaFeatureType,
  InputTableQuerySpec,
  MarkerType,
  NuFeatureType,
  SequenceFeatureTypes,
  SystemParameter,
  User,
  VariantAnalysisGroup,
} from "../graphql.js";

export const UNKNOWN_GENOME_ID = 0;
export const UNSPECIFIED_ENTITY_ID = -1;

export const CLASSIC_UI = "Classic UI";
export const WEB_UI = "Web UI";

// Licensed modules
export enum LICENSE_MODULE {
  EXPLORER = "explorer",
  TRACKER = "tracker",
  ANALYST = "analyst",
  PROCESSOR = "processor",
  ARRAY = "array",
}

// The external apps corresponding to Licensed modules
export const EXT_APP_MODULES = {
  ANALYST: { id: LICENSE_MODULE.ANALYST, name: "Analyst" },
  ARRAY: { id: LICENSE_MODULE.ARRAY, name: "Array" },
  PROCESSOR: { id: LICENSE_MODULE.PROCESSOR, name: "Processor" },
};

// License model
export enum LICENSE_MODEL {
  NAMED_USERS = "named users",
  CONCURRENT_USERS = "concurrent users",
  MIXED = "mixed model",
}

// User license model
export enum USER_LICENSE_MODEL {
  NAMED = "Named",
  CONCURRENT = "Concurrent",
}

// Errors & Exceptions
export const AUTHORISATION_EXCEPTION = "AUTHORISATION_EXCEPTION";
export const INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR";
export const NOT_FOUND_EXCEPTION = "NOT_FOUND_EXCEPTION";
export const LICENSE_SEATS_EXCEPTION = "LICENSE_SEATS_EXCEPTION";
export const AUTHENTICATION_EXCEPTION = "AUTHENTICATION_EXCEPTION";
export const SERVICE_UNAVAILABLE_EXCEPTION = "SERVICE_UNAVAILABLE_EXCEPTION";
export const VALIDATION_ERROR = "VALIDATION_ERROR";
export const REACT_CAUGHT_EXCEPTION = "REACT_CAUGHT_EXCEPTION";
export const PRIMARY_PARENT_TYPE_MISSING = "PRIMARY_PARENT_TYPE_MISSING";
export const DATA_FETCHING_EXCEPTION = "DataFetchingException";
export const MODULE_NOT_ENABLED_EXCEPTION = "MODULE_NOT_ENABLED_EXCEPTION";

// Table sorting
export const SORT_ORDER_ASC = "ASC";
export const SORT_ORDER_DESC = "DESC";

// Table constants
export const DEFAULT_PAGE_SIZE = 100;
export const MIDDLEWARE_ROW_IDS_THRESHOLD = 10000;
export const SEARCH_RESULTS_LIMIT = 100000;
export const MAX_GROUP_SIZE = 10e6 - 1;

// Cell constants
export const LOCATION_MAPPINGS_CELL_VIZ_WIDTH = 100;
export const LOCATION_MAPPINGS_CELL_VIZ_HEIGHT = 10;

// SortDialog constants
export const MAX_SORTED_COLUMNS = 10;

// Genome browser constants
export const DEFAULT_TRACK_HEIGHT = 22;
export const DEFAULT_VARIANT_TRACK_HEIGHT = 50;
export const PROFILE_TRACK_HEIGHT = 100;
export const NGS_COVERAGE_TRACK_HEIGHT = 80;
export const MIN_PROFILE_TOP_SPACE = 25;
export const DEFAULT_RANGE_PADDING_FACTOR = 0.3;
export const MIN_VIEW_RANGE_SIZE = 41;
export const CHAR_WIDTH_IN_PIXEL = 8;
export const SCALE_FACTOR_SLIDER_ZOOM = 2;
export const SCALE_FACTOR_MOUSE_WHEEL_ZOOM = 1.2;
export const MIN_SHAPE_WIDTH = 2;
export const LANE_PADDING = 5; // distance of stacked lanes
export const AXIS_HEIGHT = 20;
export const FLAG_HEIGHT = 20;
export const AXIS_TICK_COUNT = 10;
export const STANDARD_AXIS_TICK_HEIGHT = 6;
export const RANGE_PREFETCH_FRACTION = 0.5;
export const FEATURE_TABLE_DEFAULT_WIDTH = 500;
export const FEATURE_TABLE_DEFAULT_HEIGHT = 300;
export const FEATURE_TABLE_MIN_SIZE = 10;
export const FEATURE_TABLE_MAX_SIZE = 800;
export const GENOME_BROWSER_TABLE_WIDTH_MAX = 700;
export const GENOME_BROWSER_TABLE_HEIGHT_MAX = 500;
export const READ_NOTIFICATIONS_LIMIT = 20;
export const TAB_MORE_WIDTH_PX = 80;
export const TAB_MARGIN_WIDTH_PX = 15;
export const TAB_CHAR_WIDTH_PX = 8;
export const CONTIG_OVERVIEW_SELECTION_THRESHOLD = 500;
export const MIN_RANGE_WIDTH_FOR_NUFEATURE_LOADING = 8000; // load features for ranges smaller than this value only.
export const MIN_RANGE_WIDTH_FOR_MARKER_LOADING = 8000; // load markers for ranges smaller than this value only.
export const MAX_FEATURES_FOR_FEATURE_LOADING = 200; // load features only when zoomed out if the expected number of features is below this limit
export const MIN_RANGE_WIDTH_FOR_AAFEATURE_LOADING = 100000; // load features for ranges smaller than this value only.
export const MIN_RANGE_WIDTH_FOR_STANDARD_FEATURE_LOADING = 100000; // SEL6-2438:load features for ranges smaller than this value only.
export const TRACK_LABEL_OFFSET = 12;
export const EXPRESSION_LANE_GAP = 4; // the height of the gap between the feature symbol and the first expression bar
export const EXPRESSION_LANE_BAR_HEIGHT = 8; // the height of the colored bar
export const EXPRESSION_LANE_HEIGHT = EXPRESSION_LANE_BAR_HEIGHT + EXPRESSION_LANE_GAP; // the full height including some top/bottom padding
export const MAX_SELECTABLE_FEATURES = 10000;
export const MAX_BLAST_INPUT_SEQUENCES = 1000;
export const HIT_MISSING_ACCESSION = "n/a";
export const HIT_MISSING_INDEX_NOT_IN_SYNC =
  "The subject sequence of this hit does no longer exist in the database. Please update the BLAST index and rerun this job.";
export const HIT_MISSING_EXT_INDEX =
  "The subject sequence of this hit does not exist in the database. It is part of an external index.";

// Entity types
export const ENTITY_TYPE = {
  GENE: "gene",
  CONTIG: "contig",
  TRANSCRIPT: "transcript",
  PROTEIN: "protein",
  SEQUENCE: "sequence",
  GENERIC_SEQ: "genericSeq",
  TRANSCRIPT_LOCATION: "transcriptLocation",
  PROTEIN_LOCATION: "proteinLocation",
  PROBE: "probe",
  OTHER: "otherDna",
  ORGANISM: "organism",
  USER: "user",
  ORTHOLOG: "ortholog",
  DIRECTIONAL_ORTHOLOG: "directionalOrtholog",
  VARIANT: "variant",
  VCF_VARIANT: "vcfVariant",
  MUTATION: "mutation",
  GENE_MUTATION: "geneMutation",
  PROTEIN_MUTATION: "proteinMutation",
  VIEW: "view",
  REGION_FLAG: "regionFlag",
  QUERY: "query",
  BLAST_HIT: "blastHit",
  BLAST_HSP: "blastHsp",
  SAMPLE: "sample",
  SAMPLE_PARENT: "parent",
  NUFEATURE: "nuFeature",
  AAFEATURE: "aaFeature",
  SAMPLE_REPORT: "report",
  SAMPLE_ATTRIBUTE: "attribute",
  SAMPLE_TYPE: "sampleType",
  PATHWAY: "pathway",
  GENOMEASSEMBLY: "genomeAssembly",
  ANALYST_SESSION: "ssaSession",
  WORKFLOW_SESSION: "sswSession",
  ARRAY_SESSION: "sssSession",
  ANALYSIS_GROUP: "analysisGroup",
  MARKER: "marker",
  MARKER_NUS: "markerNus",
  EVENT: "playbookEvent",
  PROJECT: "project",
  PLAYBOOK: "playbook",
  BED_FEATURE: "bed",
  COORD_LIST: "coordinateList",
  COORDINATE: "coordinate",
  WORKFLOW: "workflow",
  PROJECT_FILE: "projectFile",
};

export const FEATURE_DISPLAY_NAME = {
  [ENTITY_TYPE.VIEW]: "view",
  [ENTITY_TYPE.GENE]: "gene",
  [ENTITY_TYPE.CONTIG]: "contig",
  [ENTITY_TYPE.TRANSCRIPT]: "transcript",
  [ENTITY_TYPE.PROTEIN]: "protein",
  [ENTITY_TYPE.TRANSCRIPT_LOCATION]: "transcript",
  [ENTITY_TYPE.PROTEIN_LOCATION]: "protein",
  [ENTITY_TYPE.ORGANISM]: "genome",
  [ENTITY_TYPE.USER]: "user",
  [ENTITY_TYPE.VARIANT]: "derived variant",
  [ENTITY_TYPE.VCF_VARIANT]: "variant",
  [ENTITY_TYPE.MUTATION]: "variant",
  [ENTITY_TYPE.GENE_MUTATION]: "gene",
  [ENTITY_TYPE.PROTEIN_MUTATION]: "protein",
  [ENTITY_TYPE.ORTHOLOG]: "ortholog",
  [ENTITY_TYPE.DIRECTIONAL_ORTHOLOG]: "directional ortholog",
  [ENTITY_TYPE.REGION_FLAG]: "genome flag",
  [ENTITY_TYPE.QUERY]: "query",
  [ENTITY_TYPE.BLAST_HIT]: "BLAST hit",
  [ENTITY_TYPE.BLAST_HSP]: "BLAST HSP",
  [ENTITY_TYPE.PROBE]: "probe",
  [ENTITY_TYPE.OTHER]: "other DNA",
  [ENTITY_TYPE.SEQUENCE]: "sequence",
  [ENTITY_TYPE.USER]: "user",
  [ENTITY_TYPE.SAMPLE]: "sample",
  [ENTITY_TYPE.SAMPLE_PARENT]: "sample parent",
  [ENTITY_TYPE.SAMPLE_TYPE]: "sample type",
  [ENTITY_TYPE.ANALYST_SESSION]: "analyst session",
  [ENTITY_TYPE.WORKFLOW_SESSION]: "workflow session",
  [ENTITY_TYPE.NUFEATURE]: "DNA feature",
  [ENTITY_TYPE.AAFEATURE]: "protein feature",
  [ENTITY_TYPE.MARKER]: "marker",
  [ENTITY_TYPE.MARKER_NUS]: "marker",
  [ENTITY_TYPE.EVENT]: "event",
  [ENTITY_TYPE.PLAYBOOK]: "Playbook",
  [ENTITY_TYPE.BED_FEATURE]: "BED Feature",
  [ENTITY_TYPE.GENERIC_SEQ]: "sequence",
  [ENTITY_TYPE.COORD_LIST]: "coordinate list",
  [ENTITY_TYPE.COORDINATE]: "coordinate",
  [ENTITY_TYPE.WORKFLOW]: "workflow",
  [ENTITY_TYPE.PROJECT_FILE]: "file",
};

export const FILE_TYPE = "file"; // TODO use ITEM_TYPE ENUM directly

// better make use of this util rather than accessing directly the above map !
export function getSequenceFeatureTypeDisplayName(
  tableSpec: InputTableQuerySpec,
  sequenceFeatureTypes?: SequenceFeatureTypes,
) {
  if (!tableSpec) {
    return;
  }
  // we need to get the name of the special dynamically created features from the organism data
  // (be it from the genome browser or directly from the genome)
  if (tableSpec.resultSubTypeId != null && sequenceFeatureTypes) {
    if (tableSpec.resultType === ENTITY_TYPE.NUFEATURE) {
      const nuFeature: NuFeatureType = sequenceFeatureTypes.nuFeatureTypes?.find(
        (nuFeature: NuFeatureType) => nuFeature.id === tableSpec.resultSubTypeId,
      );
      if (nuFeature) {
        return nuFeature.name;
      }
    } else if (tableSpec.resultType === ENTITY_TYPE.MARKER || tableSpec.resultType === ENTITY_TYPE.MARKER_NUS) {
      const marker: MarkerType = sequenceFeatureTypes.markerTypes?.find(
        (marker: MarkerType) => marker.id === tableSpec.resultSubTypeId,
      );
      if (marker) {
        return marker.name;
      }
    } else if (tableSpec.resultType === ENTITY_TYPE.AAFEATURE) {
      const aaFeature: AaFeatureType = sequenceFeatureTypes.aaFeatureTypes?.find(
        (aaFeature: AaFeatureType) => aaFeature.id === tableSpec.resultSubTypeId,
      );
      if (aaFeature) {
        return aaFeature.name;
      }
    } else if (tableSpec.resultType !== ENTITY_TYPE.BED_FEATURE) {
      // console.error("Cannot find feature display name for tableSpec " + JSON.stringify(tableSpec, null, 2));
    }
    // do not return, continue using the other info we have
  }

  const { resultType, queryType } = tableSpec;

  if (!queryType) {
    return FEATURE_DISPLAY_NAME[resultType];
  } else {
    if (resultType === ENTITY_TYPE.GENE_MUTATION && queryType === ENTITY_TYPE.GENE) {
      return FEATURE_DISPLAY_NAME[ENTITY_TYPE.MUTATION];
    }
    if (resultType === ENTITY_TYPE.PROTEIN_MUTATION && queryType === ENTITY_TYPE.PROTEIN) {
      return FEATURE_DISPLAY_NAME[ENTITY_TYPE.MUTATION];
    }
    return FEATURE_DISPLAY_NAME[resultType];
  }
}

export const SEARCH_RESTRICTION_TYPES = {
  GENOME: "genome",
  SPECIES: "species",
  DATABASE: "database",
};

// Track types in the genome browser
export const TRACK_TYPE = {
  GENE: ENTITY_TYPE.GENE,
  TRANSCRIPT_LOCATION: ENTITY_TYPE.TRANSCRIPT_LOCATION,
  PROTEIN_LOCATION: ENTITY_TYPE.PROTEIN_LOCATION,
  VARIANT: ENTITY_TYPE.VARIANT,
  VCF_VARIANT: ENTITY_TYPE.VCF_VARIANT,
  SEQUENCE: "sequence", // not really the same as ENTITY_TYPE.SEQUENCE
  REGION_FLAG: ENTITY_TYPE.REGION_FLAG,
  BLAST_ALIGNMENT: "blastAlignment",
  PROFILE: "profile",
  NGS: "ngs",
  BED: ENTITY_TYPE.BED_FEATURE,
  COORDINATE: ENTITY_TYPE.COORDINATE,
  NUFEATURE: ENTITY_TYPE.NUFEATURE,
  AAFEATURE: ENTITY_TYPE.AAFEATURE, // e.g. PFAM
  MARKER_NUS: ENTITY_TYPE.MARKER_NUS,
};

export enum TRACK_SUBTYPE {
  NGS_COVERAGE = "NGS_COVERAGE",
  NGS_PILEUP = "NGS_PILEUP",
}

// Variant type names as in MutationTypeEnum on the server
export const VARIANT_TYPE = {
  POINT: "point",
  DELETION: "deletion",
  INSERTION: "insertion",
  INDEL: "indel",
  TRANSLOCATION_DELETION: "translocation-deletion",
  TRANSLOCATION_INSERTION: "translocation-insertion",
};

// samples
export const DEFAULT_SAMPLE_STATUS_SET_ID = 1;

// Table Query Specification's queryType (applies only when the idType = DATATABLE_ID_TYPE.ENTITY)
export const DATATABLE_PARENT_TYPE = {
  ORGANISM: ENTITY_TYPE.ORGANISM,
  CONTIG: ENTITY_TYPE.GENERIC_SEQ,
  PROTEIN: ENTITY_TYPE.PROTEIN,
  FILE: FILE_TYPE,
  COORD_LIST: ENTITY_TYPE.COORD_LIST,
};

export const DATATABLE_ID_TYPE = {
  // on middle tier, corresponds to IdTypeEnum
  GROUP: "group",
  ENTITY: "entity",
};

// Color set for the color picker
export const CHART_COLOR_SET = [
  "#d53e4f",
  "#fdae61",
  "#66c2a5",
  "#5e4fa2",
  "#3288bd",
  "#9e0142",
  "#fee08b",
  "#abdda4",
  "#f46d43",
  "#e6f598",
];

// Color set from TSD for user thumbnails and projects
export const BASE_COLOR_SET = ["#5e4fa2", "#3288bd", "#66c2a5", "#a9c44f", "#b3215d", "#e54658", "#f57f5b", "#fbc938"];

// Chart types
export type ChartType = "line" | "bar" | "scatter" | "stacked-bar";
export const CHART_TYPE: Record<string, ChartType> = {
  BAR: "bar" as ChartType,
  STACKED_BAR: "stacked-bar" as ChartType,
  LINE: "line" as ChartType,
};

// "unknown" genome
export const UNKNOWN_GENOME_NAME = "-";

// Cell types
export const CELL_TYPE = {
  CHECKBOX: "checkbox", // for selection column
  STRING: "string",
  SCIENTIFIC: "scientific",
  TEXT: "text",
  BOOLEAN: "boolean",
  NUMBER: "number",
  INTEGER: "integer",
  DATE: "date",
  DATE_TIME: "date_time",
  STRING_LIST: "string_list",
  NESTED_STRING_LIST: "nested_string_list",
  NUMBER_LIST: "number_list",
  OBJECT_LIST: "object_list",
  COLOR: "color",
  VARIANT_PROFILE: "variant_profile",
  FILE: "file",
  FILENAME: "filename", // artificial type on the front-end, since needs special sorting treatment
  LOCKED: "locked", // artificial type on the front-end, only used in sample editing grids
  ORGANISM: "organism",
};

export const NOTIFICATION_STATUS = {
  TERMINATED: "Terminated",
  ERROR: "Error",
  QUEUED: "Queued",
  COMPLETE: "Complete",
  IN_PROGRESS: "In Progress",
};

export const WORKFLOW_NOTIFICATION_SUBTYPE = {
  SAMPLE_STATUS_CHANGE: "status change",
  PLAYBOOK_EVENT_CHANGE: "event change",
  WORKFLOW_EVENT_CHANGE: "workflow event change",
};

/**
 * This has been introduced to differentiate between
 * organism table requests in normal and admin contexts.
 */
export const TABLE_CONTEXT_TYPE = {
  DEFAULT: "default",
  ADMIN: "admin",
};

// Variant effects
export const VARIANT_EFFECT = {
  UNKNOWN: "Unknown",
  GENETIC_ELEMENTS_MOVED: "Genetic elements moved",
  SYNONYMOUS_CODING: "Synonymous coding",
  SYNONYMOUS_STOP: "Synonymous stop",
  SYNONYMOUS_START: "Synonymous start",
  NON_SYNONYMOUS_CODING: "Non-synonymous coding",
  NON_SYNONYMOUS_START: "Non-synonymous start",
  NON_SYNONYMOUS_STOP: "Non-synonymous stop",
  CODON_INSERTION: "Codon insertion",
  CODON_DELETION: "Codon deletion",
  STOP_LOST: "Stop lost",
  EXON_DELETED: "Exon deleted",
  UTR_5_DELETED: "5'-UTR deleted",
  UTR_3_DELETED: "3'-UTR deleted",
  STOP_GAINED: "Stop gained",
  CODON_CHANGE_PLUS_CODON_INSERTION: "Codon change plus codon insertion",
  CODON_CHANGE_PLUS_CODON_DELETION: "Codon change plus codon deletion",
  CODON_CHANGE: "Codon change",
  START_GAINED: "Start gained",
  START_LOST: "Start lost",
  SPLICING_POTENTIALLY_DISTURBED: "Splicing potentially disturbed",
  SPLICE_SITE_DONOR: "Splice site donor",
  SPLICE_SITE_ACCEPTOR: "Splice site acceptor",
  GENETIC_ELEMENTS_LOST: "Genetic elements lost",
  GENETIC_ELEMENTS_GAINED: "Genetic elements gained",
  FRAME_SHIFT: "Frame shift",
};

// Key values and names.
//   The values are actually event key names
export const KEY: any = {
  TAB: "Tab",
  RETURN: "Enter",
};

// Key code values and names
export enum KEYCODE {
  TAB = 9,
  RETURN = 13,
}

// Special users and groups
export const PUBLIC_USER: User = {
  id: UNSPECIFIED_ENTITY_ID,
  username: "public",
  firstName: null,
  lastName: null,
  groupType: ENTITY_TYPE.USER,
  email: null,
  capabilities: null,
  display: "public",
  lastUse: null,
  accountStatus: null,

  params: {
    homeItemSortOrder: null,
    lastSaveTarget: null,
    projectItemSortOrder: null,
    firstLogin: false,
    showTips: false,
    lastAdvancedSearchType: ENTITY_TYPE.ORGANISM,
    lastAdvancedSearchSubTypeId: UNSPECIFIED_ENTITY_ID,
    wrapColumnHeaders: false,
    referenceGenome: null,
    showFlagsTrack: true,
    useSpecificFlagsColoring: false,
    showCommunityAnnotations: false,
    defaultTracks: null,
  },
  userGroups: null,
  picture: null,
  roles: [],
  isActive: true,
};

// Field length constants
export const MAX_LENGTH: any = {
  PROJECT: {
    NAME: 50,
    DESCRIPTION: 250,
    STATUS_NOTES: 2000,
    END_NOTES: 2000,
    COMMENT: 500,
  },
  GROUP: {
    NAME: 150,
    DESCRIPTION: 500,
  },
  FLAG: {
    NAME: 100,
    DESCRIPTION: 2000,
  },
  VIEW: {
    NAME: 100,
    DESCRIPTION: 500,
  },
  RESOURCE: {
    NAME: 150,
    URL: 1000,
    DESCRIPTION: 500,
  },
  USERGROUP: {
    NAME: 55,
    DESCRIPTION: 150,
  },
  ROLE: {
    NAME: 30,
    DESCRIPTION: 150,
  },
  ATTRIBUTE: {
    NAME: 500,
    GROUP_NAME: 150,
    PATTERN: 500,
    PATTERN_HINT: 150,
    FORMULA: 4000,
    COLORING_RULE: 4000,
    SQL_STATEMENT: 4000,
    IDACCESSOR: 500,
    VALUE: 500,
    TERM: 100,
    DEFAULT_TEXT: 500,
    TEMPLATE: 1000,
  },
  USER: {
    USERID: 50,
    ORACLE_USERID: 30,
    FIRSTNAME: 50,
    LASTNAME: 50,
    EMAILADRESS: 50,
  },
  GENOME: {
    NAME: 100,
    DESCRIPTION: 2000,
    IDENTIFIER: 50,
    SPECIES: 150,
    STRAIN: 150,
    DATABASE: 50,
    TAXONOMY: 50,
    KINGDOM: 50,
  },
  CARD: {
    TITLE: 50,
  },
  DATABASE: {
    NAME: 50,
    MNEMONIC: 20,
  },
  ORGANISM_PROPERTY: {
    NAME: 150,
    GROUP_NAME: 150,
    TYPE_NAME: 150,
    DESCRIPTION: 500,
  },
  PLAYBOOKS: {
    CATEGORY_NAME: 150,
  },
  EVENT: {
    NAME: 150,
    DESCRIPTION: 500,
  },
  COORD_LIST: {
    NAME: 150,
    DESCRIPTION: 500,
  },
};

export const MAX_SEQUENCE_EXPORT_LENGTH = 10000;

export const MAX_SCIENTIFIC_NUMBER_EXPONENT = 99;
export const MIN_SCIENTIFIC_NUMBER_EXPONENT = -999;

/**
 * Types of saved views (middle tier: ViewTypeEnum.java).
 * The value of type triggers the respective reducers to repopulate the application state.
 */
export interface IViewType {
  type: string;
  title: string;
}

/**
 * Types of saved views (middle tier: ViewTypeEnum.java).
 * The value of type triggers the respective reducers to repopulate the application state.
 */
export const VIEW = {
  GENOME_VIEW: {
    type: "GENOME_VIEW",
    title: "Genome View",
  },
  SYNTENY_VIEW: {
    type: "SYNTENY_VIEW",
    title: "Synteny View",
  },
  DATA_VIEW: {
    type: "DATA_VIEW",
    title: "Data View",
  },
  BLAST_JOB_VIEW: {
    type: "BLAST_JOB_VIEW",
    title: "BLAST View",
  },
  ENTITY_SUMMARY_VIEW: {
    type: "ENTITY_SUMMARY_VIEW",
    title: "Summary View",
  },
  TABLE_VIEW: {
    type: "TABLE_VIEW",
    title: "Table View",
  },
  CONTENT_BROWSER_VIEW: {
    type: "CONTENT_BROWSER_VIEW",
    title: "Content Summary",
  },
  USER_GROUP_ADMIN_VIEW: {
    type: "USER_GROUP_ADMIN_VIEW",
    title: "User Group Summary",
  },
};

export const GENOME_BROWSER_SIZE_OPTIONS = {
  XXSMALL: "xxsmall",
  XSMALL: "xsmall",
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
  XLARGE: "xlarge",
};

export const ANNOTATION_TYPE = {
  NUMERICAL: "numerical",
  CATEGORICAL: "categorical",
  DURATION: "duration",
};

export const DURATION_UNIT = {
  SECONDS: "seconds",
  MINUTES: "minutes",
  HOURS: "hours",
  DAYS: "days",
  WEEKS: "weeks",
  MONTHS: "months",
  YEARS: "years",
};

export enum PLAYBOOK_CLASS {
  BLAST = "blast",
  SAMPLE = "sample",
  NGS = "ngs",
  RANGE = "range",
  REPORT = "report",
  ALIGNMENT = "alignment",
}

export enum PLAYBOOK_TYPE {
  BLAST = 1,
  NGS_ANALYSIS_WITH_REGISTERED_SAMPLES = 2,
  SAMPLE_REGISTRATION = 3,
  NGS_ANALYSIS = 4,
  RANGE_ANALYSIS = 5,
  NEXTFLOW_ANALYSIS = 6,
  NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES = 7,
  REPORT_FROM_VARIANTS = 8,
  REPORT_FROM_COORDINATES = 9,
  ALIGNMENT = 10,
  PROCESSOR = 11,
}

export enum PLAYBOOK_TYPE_LABELS {
  BLAST = "BLAST",
  NGS_ANALYSIS_WITH_REGISTERED_SAMPLES = "Classic Processor with Samples",
  NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES = "Nextflow Pipeline with Samples",
  SAMPLE_REGISTRATION = "Sample Registration",
  NGS_ANALYSIS = "Classic Processor",
  RANGE_ANALYSIS = "Classic Genomic Range",
  NEXTFLOW_ANALYSIS = "Nextflow Pipeline",
  REPORT_FROM_VARIANTS = "Variant Report",
  REPORT_FROM_COORDINATES = "Coordinate Report",
  PROCESSOR = "Processor",
  // PROCESSOR_WITH_REGISTERED_SAMPLES = "Processor with Samples",
}

// Maximum allowed range length to be provided to a variant alignment playbook
export const MAX_PLAYBOOK_RANGE_LENGTH = 10000;

export enum SAMPLE_TRACKING {
  REGISTER = "register",
  USE_EXISTING = "use-existing",
}

// correspond to JobType DTO, only for input DTOs !
export const JOB_TYPES = {
  BLAST: "blast",
};

// CLU_TOOLS
export const JOB_TOOLS = {
  // BLAST tools are the BLAST methods
  NCBI: "ncbiagent",
  WORKFLOWEXECUTION: "workflow-execution",
};

export enum BLAST_METHOD {
  BLASTN = "blastn",
  BLASTP = "blastp",
  TBLASTN = "tblastn",
  BLASTX = "blastx",
}

export const BLAST_SUB_VIEW = {
  SUMMARY: "summary",
  HITS: "hits",
  HSPS: "hsps",
  ALIGNMENTS: "alignments",
  // HITSUMMARY : "hitsummary",
  // GENOME : "genomeview",
};

export type StrandType = "+" | "-" | ".";
export const STRAND_TYPE = {
  PLUS: "+",
  MINUS: "-",
  UNKNOWN: ".",
};

// in fact, these are sequence-related group types (as the DB defines them)
export const SEQUENCE_TYPE = {
  GENOMIC: "genomic",
  TRANSCRIPT: "transcript",
  PROTEIN: "protein",
  PROBE: "probe",
  OTHER_DNA_RNA: "other_dna_rna",
};

export const SEQUENCE_GROUP_TYPE = {
  [SEQUENCE_TYPE.GENOMIC]: "Contig & Vector",
  [SEQUENCE_TYPE.TRANSCRIPT]: "Transcript",
  [SEQUENCE_TYPE.PROTEIN]: "Protein",
  [SEQUENCE_TYPE.PROBE]: "Probe & Probeset",
  [SEQUENCE_TYPE.OTHER_DNA_RNA]: "Other",
};

// Use JobStatusEnum instead
export const EVENT_STATUS = {
  INITIALIZING: "Initializing",
  QUEUED: "Queued",
  RUNNING: "In Progress",
  COMPLETED: "Complete",
  ERROR: "Error",
  TERMINATED: "Terminated",
};

// To translate  features/entityTypes to monomers use
// mapFeatureTypeToMonomer( entityType: key of ENTITY_TYPE ): key of MONOMER_TYPE
export const MONOMER_TYPE = {
  NU: "nucleotide",
  AA: "amino acid",
};

export const TRACKER_STATUS_CLASS_ID = {
  READY: 1,
  IN_PROGRESS: 2,
  TERMINAL: 3,
};

export const TRACKER_STATUS = {
  ERROR: "Error",
  NEW: "New",
  PROCESSING: "Processing",
  COMPLETE: "Complete",
  READY: "Ready",
};

export enum FILE_STATUS {
  UPLOAD_PENDING = "upload pending",
  UPLOAD_FAILED = "upload failed",
  UPLOAD_COMPLETE = "upload complete",
  UPLOAD_CANCELED = "upload canceled",
  VALIDATION_PENDING = "validation pending",
  VALIDATION_COMPLETE = "validation complete",
  VALIDATION_FAILED = "validation failed",
  VALIDATION_WAITING = "validation waiting",
}

// taken from SequencingTypeEnum Java Class:
//   NOT_SEQUENCED("not_sequenced", "Not Sequenced"),
//   RE_SEQUENCED_VCF("re_sequenced_vcf", "Re-sequenced VCF"),
//   RE_SEQUENCED("re_sequenced", "Re-sequenced"),
//   SEQUENCED_ASSEMBLY("sequenced", "Sequenced Assembly");

// On the front-end side we are dealing with the value, not the key
// Also used with SEQUENCING_TYPE_LABELS (content-browser.ts)
export const SEQUENCING_TYPE_KEY = {
  NOT_SEQUENCED: "not_sequenced",
  RE_SEQUENCED_VCF: "re_sequenced_vcf",
  RE_SEQUENCED: "re_sequenced",
  SEQUENCED_ASSEMBLY: "sequenced_assembly",
  UPLOADED: "vector",
  VIRTUAL: "virtual",
  PROTEIN_SYNTENY: "protein_synteny_set",
  GENE_SYNTENY: "gene_synteny_set",
  PANGENOME: "pangenome",
  UNKNOWN: "unknown",
};

export const SEQUENCING_TYPE = {
  NOT_SEQUENCED: "Not Sequenced",
  RE_SEQUENCED_VCF: "Re-sequenced VCF",
  RE_SEQUENCED: "Re-sequenced",
  SEQUENCED_ASSEMBLY: "Sequenced Assembly",
  PROTEIN_SYNTENY: "Protein Synteny Set",
  GENE_SYNTENY: "Gene Synteny Set",
  PANGENOME: "Pangenome Graph",
};

export const FEATURE_ANNOTATION = {
  GENES: "genes",
  TRANSCRIPTS: "transcripts",
  PROTEINS: "proteins",
  EC_COVERAGE: "ec_coverage",
};

export const COLOR_SCALE_MIN_BIT_SCORE = 50;
export const COLOR_SCALE_MAX_BIT_SCORE = 300;

export const HISTOGRAM_LINE_LIMIT = 20;

// keys for the menu items
export const GROUP_BY_ATTRIBUTE_SPECIES = "species";
export const GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE = "sequencingType";
export const GROUP_BY_ATTRIBUTE_FEATURES = "featureAnnotation";
export const GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP = "variantAnalysisGroup";
export const GROUP_BY_ATTRIBUTE_PROTCOMP = "protcomp";
export const GROUP_BY_ATTRIBUTE_USER_GROUP = "userGroup";
export const GROUP_BY_ATTRIBUTE_CREATION_TIME = "creationTime";
export const GROUP_BY_TAXONOMIC_GROUP = "taxonomy";

// to be completed upon need
export const ORGANISM_TABLE_COLUMNS = {
  NAME: "name",
  SPECIES: "species.name",
  SEQUENCING_TYPE: "sequencingType",
  NO_CONTIGS: "genomeStatistics.contigCount",
  NO_GENES: "genomeStatistics.geneCount",
  NO_TRANSCRIPTS: "genomeStatistics.transcriptsCount",
  NO_PROTEINS: "genomeStatistics.proteinsCount",
  EC_COVERAGE: "genomeStatistics.ecCoverage",
  VARIANT_ANALYSIS_GROUP: "variantAnalysisGroup.name",
  REF_GENOME: "isReference",
  USER_GROUPS: "userGroups.name",
  TAXONOMY: "taxonomy",
  CREATION_DATE: "created",
};

// to be completed upon need
export const VARIANT_TABLE_COLUMNS = {
  NAME: "name",
  TYPE: "variantType",
  EFFECT: "effect",
  PROFILE: "profile.symbol",
  VCF_PROFILE: "profileVariant.symbol",
  VARIANT_GENOMES: "vcfVariantInstances.varGenome.name",
};

// to be completed upon need
export const USER_TABLE_COLUMNS = {
  USERNAME: "username",
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  EMAIL: "email",
  USER_GROUPS: "userGroups.name",
};

// to be completed upon need
export const SAMPLE_TYPE_TABLE_COLUMNS = {
  NAME: "name",
  CREATED: "created",
  DESCRIPTION: "description",
  PROJECT_GROUPS: "projectGroups",
};

// to be completed upon need
export const ATTRIBUTE_TABLE_COLUMNS = {
  NAME: "attributeName",
};

export const SESSION_TABLE_COLUMNS = {
  MODIFIED: "modified",
};

export const EVENT_TABLE_COLUMNS = {
  CREATED: "created",
};

export const SAMPLE_TABLE_COLUMNS = {
  CREATED: "created",
};

// to be completed upon need, see fragments.ts:GridFragments.blastHitGrid & queryParams.withSequence
export const BLAST_HITS_TABLE_COLUMNS = {
  PROTEIN_NAMES: "protein.proteinNames",
  SUBJECT_SEQUENCE: "target.sequence",
  ALIGNED_SUBJECT_SEQUENCE: "bestHsp.targetSequence",
  HSP_TARGET_SEQUENCE: "targetSequence",
  OVERLAPPING_GENE_SEQUENCE: "bestHsp.relatedGenes.sequence",
  HSP_GENE_SEQUENCE: "relatedGenes.sequence",
};

export const CONTENT_BROWSER_FEATURE_TYPE_ORGANISMS = "organisms";
export const CONTENT_BROWSER_FEATURE_TYPE_SAMPLE_TYPES = "sampleTypes";
export const CONTENT_BROWSER_FEATURE_TYPE_USERS = "users";

export const MAX_NUMBER_OF_CONTENT_BROWSER_ENTRIES = 6; // before we open the 'Others' entries

export const ROW_HEIGHT = 32;
export const SUBROW_HEIGHT = 19;
export const HEADER_HEIGHT = 40;
export const HEADER_GROUP_HEIGHT = 27;
export const MAX_HEADER_HEIGHT = 500;

export const COLUMN_MENU_UPWARDS_HEIGHT_THRESHOLD = 330;

export const MINIMUM_BLAST_SEQUENCE_LENGTH = 7;
export const MAXIMUM_BLAST_SEQUENCE_LENGTH = 1000000;

export const GENOME_TRACK_COLOR = {
  LIGHTER: "#fafafa",
  LIGHT: "#F1F3F4",
  DEFAULT: "#F1F3F4",
  MATE_DIFF_CHR: "#F1E3E4",
  SINGLE: "#D1D3D4", // NGS: no first/second pair
  GREY: "#dddddd",
  CLIPPING: "#b6bAbf", // NGS: Clipping (hard and soft)
  DARK: "#242526", // NGS: deletion is a line, addition is a shape
  STRAND_MINUS: "#CC0000",
  STRAND_PLUS: "#0000CC",
  GRADIENT_START: "#FF0000",
  GRADIENT_STOP: "#00FF00",
  LOW_MAPPING_QUALITY: "#990099",
  HIGH_MAPPING_QUALITY: "#AAAAAA",
};

export const SEQ_RESIDUE_AA_COLORS = {
  ["r"]: "#bfc8ff",
  ["k"]: "#d5ccff",
  ["e"]: "#ffcccc",
  ["d"]: "#ffd6d6",
  ["i"]: "#ffffb3",
  ["l"]: "#ffffb3",
  ["v"]: "#ffffbf",
  ["a"]: "#ffffcc",
  ["c"]: "#e7f9bb",
  ["h"]: "#c0f5fc",
  ["m"]: "#bbfa9b",
  ["n"]: "#ffc9e2",
  ["q"]: "#ffdef2",
  ["f"]: "#d8d9a5",
  ["y"]: "#a8ebf7",
  ["w"]: "#cbe6f7",
  ["s"]: "#fcd4fc",
  ["t"]: "#f0e4ef",
  ["g"]: "#e1ebea",
  ["p"]: "#cee0e0",
  ["*"]: "#d88fae",
};

export const SEQ_RESIDUE_NU_COLORS = {
  ["a"]: "hsl(121, 46%, 72%)",
  ["t"]: "hsl(16, 100%, 76%)",
  ["c"]: "hsl(225, 66%, 79%)",
  ["g"]: "hsl(51, 100%, 78%)",
  ["n"]: "#dddddd",
  ["clippping"]: GENOME_TRACK_COLOR.CLIPPING, // Hard & Soft clipping in CIGAR string
};

export const EXPORT_FILE_FORMATS = {
  XLSX_FORMAT: "xlsx",
  FASTA_FORMAT: "fasta",
  RAW_FORMAT: "raw",
  PNG_FORMAT: "png",
  GDA_FORMAT: "gda",
  GDC_FORMAT: "gdc",
  GFF_FORMAT: "gff",
  GBK_FORMAT: "gbk",
  VCF_FORMAT: "vcf",
};

export const FASTA_HEADER_TEMPLATE_OPTIONS_DEFAULT = "PROFILER_IDENTIFIERS";
export const FASTA_HEADER_TEMPLATE_OPTIONS: Record<string, ISelectOption> = {
  SELECTOR_IDENTIFIERS: { value: "selectorIdentifiers", label: "Complete Identifier" },
  PROFILER_IDENTIFIERS: { value: "profilerIdentifiers", label: "Accession Identifier" },
  VISIBLE_COLUMNS: { value: "visibleColumns", label: "Selected Columns" },
};

// Words which have to stay in upper case in lower case text for display.
export const defaultMapOfAcronyms = {
  NCBI: "NCBI",
  EMBL: "Embl",
  PDB: "PDB",
  EC: "EC",
  GENEBANK: "GeneBank",
  GENEDATA: "Genedata",
  NGS: "NGS",
};

// to be completed upon need
export const DATA_VIEWER_MODE = {
  DATASET: 1,
  GROUP: 2,
};

// SAMPLE SCHEMA
// sample columns set
export const COS_ANNOTATION_ID = 1;
export const COS_ANALYTICAL_ID = 2;
export const COS_PARENT_ID = 3;
export const COLUMN_SET = {
  COS_ANNOTATION_ID: { id: 1, name: "Annotation" },
  COS_ANALYTICAL_ID: { id: 2, name: "Analytical" },
  COS_PARENT_ID: { id: 3, name: "Parent" },
};

export const MAX_FREE_TEXT_LENGTH = 500;
export const MAX_NUMBER_LENGTH = 40;

export const SYSTEM_PARAMS: SystemParameter = {
  // we set the default until these values are updated by config (REST call)
  projectLabel: "project",
};

export const BLAST_RESULT_FIELDS = {
  QUERY_ACCESSION: "queryAccession",
  QUERY_DESCRIPTION: "queryDescription",
  QUERY_LENGTH: "queryLength",
  HIT_DESCRIPTION: "hitDescription",
  HIT_ACCESSION: "hitAccession",
  HIT_DATABASE: "hitDatabase",
  HIT_MAX_SCORE: "hitMaxScore",
  HIT_TOTAL_SCORE: "hitTotalScore",
  HIT_QUERY_COVER: "hitQueryCover",
  HIT_E_VALUE: "hitEvalue",
  HIT_IDENT: "hitIdentities",
};

export enum ATTRIBUTE_TYPE {
  FREE_TEXT = 1,
  CONTROLLED_TEXT = 2,
  FREE_NUMERICAL = 3,
  CONTROLLED_NUMERICAL = 4,
  CALCULATED = 5,
  DATE = 6,
  HYPERLINK = 7,
  BOOLEAN = 8,
  DYNAMIC_TERMS = 9,
  DERIVED_TERMS = 11,
  FILE = 12,
  ORGANISM = 13,
  FILE_OR_FOLDER = 20, // stems from Workflow params
  GENOME_SELECTION = 21, // stems from Workflow params
  FOLDER = 23, // stems from Workflow params
  GENOMIC_COORDINATES = 24, // stems from Workflow params
}

export const ATTRIBUTE_TYPE_OPTIONS: ISelectOption[] = [
  { value: ATTRIBUTE_TYPE.FREE_TEXT, label: "Free Text" },
  { value: ATTRIBUTE_TYPE.CONTROLLED_TEXT, label: "Controlled Text" },
  { value: ATTRIBUTE_TYPE.FREE_NUMERICAL, label: "Free Numerical" },
  { value: ATTRIBUTE_TYPE.CALCULATED, label: "Calculated" },
  { value: ATTRIBUTE_TYPE.DATE, label: "Date" },
  { value: ATTRIBUTE_TYPE.HYPERLINK, label: "Hyperlink" },
  { value: ATTRIBUTE_TYPE.BOOLEAN, label: "Boolean" },
  { value: ATTRIBUTE_TYPE.FILE, label: "File" },
  { value: ATTRIBUTE_TYPE.ORGANISM, label: "Organism" },
  { value: ATTRIBUTE_TYPE.DYNAMIC_TERMS, label: "Dynamic Terms" },
  { value: ATTRIBUTE_TYPE.DERIVED_TERMS, label: "Derived Value" },
];

export const WORKFLOW_FIELD_TYPE_OPTIONS: ISelectOption[] = ATTRIBUTE_TYPE_OPTIONS.concat([
  { value: ATTRIBUTE_TYPE.FILE_OR_FOLDER, label: "File or Folder" },
  { value: ATTRIBUTE_TYPE.GENOME_SELECTION, label: "Genome Selection" },
  { value: ATTRIBUTE_TYPE.FILE, label: "File" },
  { value: ATTRIBUTE_TYPE.FOLDER, label: "Folder" },
  { value: ATTRIBUTE_TYPE.GENOMIC_COORDINATES, label: "Region Coordinates" },
]);

// values from DB table clu_file_Type
export enum ATTRIBUTE_FILE_TYPES {
  ANY = 9,
  ALIGNED_READ_DATA = 6,
  UNALIGNED_READ_DATA = 5,
}

export const SAM_FILE_TYPE_OPTIONS: ISelectOption[] = [
  { value: ATTRIBUTE_FILE_TYPES.ANY, label: "Any File or Folder" },
  { value: ATTRIBUTE_FILE_TYPES.UNALIGNED_READ_DATA, label: "Unaligned Reads" },
  { value: ATTRIBUTE_FILE_TYPES.ALIGNED_READ_DATA, label: "Aligned Reads" },
];

export const SAMPLE_FIELDS = {
  SAMPLE_NAME: "name",
  SAMPLE_TYPE: "type.name",
};

export enum EDIT_GRID_COLUMN_ACTIONS {
  CLEAR_ALL = "clear_all",
  AUTO_INCREMENT = "auto_increment",
  AUTO_COPY = "auto_copy",
  AUTOFILL_FILES = "autofill_files",
}

export const PFAM_AA_FEATURE_TYPE_ID = 10;

export const GENOME_TYPE_REFERENCE = "Reference";

export enum NF_PLAYBOOK_SCREEN_IDS {
  NF_BASICS_SCREEN = "nfBasicsScreen",
  NF_WORKFLOW_PARAMS_SCREEN = "nfWorkflowParamsScreen",
  NF_EDIT_SAMPLES_SCREEN = "nfEditSamplesScreen",
  NF_GENOME_SELECTION_SCREEN = "nfGenomeSelectionScreen",
  NF_REVIEW_SCREEN = "nfReviewScreen",
  NF_PROCESSOR_PARAMS_SCREEN = "nfProcessorParamsScreen",
}
export const NF_PLAYBOOK_EXECUTION_SCREENS: Dictionary<Partial<IPlaybookPage>> = {
  [NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN]: {
    pageIdentifier: NF_PLAYBOOK_SCREEN_IDS.NF_BASICS_SCREEN,
    title: "Submission Info",
    label: "Submission Info",
    description: "Select a destination for the files and results that are generated for this analysis.",
  },
  [NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN]: {
    pageIdentifier: NF_PLAYBOOK_SCREEN_IDS.NF_WORKFLOW_PARAMS_SCREEN,
    title: "Input Data",
    label: "Analysis Input",
    description: "Provide the inputs for your analysis.",
  },
  [NF_PLAYBOOK_SCREEN_IDS.NF_EDIT_SAMPLES_SCREEN]: {
    pageIdentifier: NF_PLAYBOOK_SCREEN_IDS.NF_EDIT_SAMPLES_SCREEN,
    title: "Samples",
    label: "Sample Input",
    description: "Select the samples for your analysis.",
  },
  [NF_PLAYBOOK_SCREEN_IDS.NF_GENOME_SELECTION_SCREEN]: {
    pageIdentifier: NF_PLAYBOOK_SCREEN_IDS.NF_GENOME_SELECTION_SCREEN,
    title: "Genomes",
    label: "Genomes",
    description: "Select genomes to include in the analysis.",
  },
  [NF_PLAYBOOK_SCREEN_IDS.NF_REVIEW_SCREEN]: {
    pageIdentifier: NF_PLAYBOOK_SCREEN_IDS.NF_REVIEW_SCREEN,
    title: "Review",
    label: "Input Review",
    description: "Check your selections.",
  },
  [NF_PLAYBOOK_SCREEN_IDS.NF_PROCESSOR_PARAMS_SCREEN]: {
    pageIdentifier: NF_PLAYBOOK_SCREEN_IDS.NF_PROCESSOR_PARAMS_SCREEN,
    title: "Workflow Data",
    label: "Analysis Input",
    description: "Provide the inputs for your analysis.",
  },
};

export enum SAMPLE_LOCKING_ACTIONS {
  LOCK = "lock",
  UNLOCK = "unlock",
}

export enum SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS {
  //SETUP_SCREEN = "setupScreen",
  PARENT_SCREEN = "parentScreen",
  NGS_DATA_SCREEN = "ngsDataScreen",
  GRID_SCREEN = "gridScreen",
  REVIEW_SCREEN = "reviewScreen",
  SELECT_SAMPLE_SCREEN = "selectSampleScreen",
  RANGE_SCREEN = "rangeScreen",
  //STATUS_SCREEN = "statusScreen",
}

export const SAMPLE_PLAYBOOK_EXECUTION_SCREENS: Dictionary<Partial<IPlaybookPage>> = {
  [SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.PARENT_SCREEN]: {
    pageIdentifier: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.PARENT_SCREEN,
    title: "Submission Info",
    description: "Select a destination for the files and results that are generated for this analysis.",
  },
  [SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.NGS_DATA_SCREEN]: {
    pageIdentifier: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.NGS_DATA_SCREEN,
    title: "NGS Data",
    label: "NGS Analysis Input",
    description: "Provide the NGS Inputs for your analysis.",
  },
  [SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.GRID_SCREEN]: {
    pageIdentifier: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.GRID_SCREEN,
    title: "Enter Data",
  },
  [SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.REVIEW_SCREEN]: {
    pageIdentifier: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.REVIEW_SCREEN,
    title: "Review",
  },
  [SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.SELECT_SAMPLE_SCREEN]: {
    pageIdentifier: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.SELECT_SAMPLE_SCREEN,
    title: "Choose Samples and Enter Data",
  },
  [SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.RANGE_SCREEN]: {
    pageIdentifier: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.RANGE_SCREEN,
    title: "Range Info",
    description: "Enter the range settings you want to analyze.",
  },
};

export enum PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS {
  BASICS_INFO_SCREEN = "basicsInfoScreen",
  EXPERIMENT_INFO_SCREEN = "experimentInfoScreen",
  NF_EXPERIMENT_INFO_SCREEN = "nextflowExperimentInfoScreen",
  P2_EXPERIMENT_INFO_SCREEN = "p2ExperimentInfoScreen",
  NF_ACTIVITIES_CONFIG_SCREEN = "nextflowActivitiesConfigurationScreen",
  SAMPLE_INFO_SCREEN = "sampleInfoScreen",
  NGS_RESULTS_SCREEN = "ngsResultsScreen",
  SAMPLE_STATUS_SCREEN = "sampleStatusScreen",
  REPORT_SCREEN = "reportScreen",
}

export const PLAYBOOK_CONFIGURATION_WIZARD_SCREENS: Dictionary<Partial<IPlaybookPage>> = {
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.BASICS_INFO_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.BASICS_INFO_SCREEN,
    title: "Define the Basics",
    label: "Basic Information About This Playbook",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.EXPERIMENT_INFO_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.EXPERIMENT_INFO_SCREEN,
    title: "Collect Experiment Information",
    label: "Experiment Information",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_EXPERIMENT_INFO_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_EXPERIMENT_INFO_SCREEN,
    title: "Collect Experiment Information",
    label: "Experiment Information",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.P2_EXPERIMENT_INFO_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.P2_EXPERIMENT_INFO_SCREEN,
    title: "Collect Experiment Information",
    label: "Experiment Information",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_ACTIVITIES_CONFIG_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NF_ACTIVITIES_CONFIG_SCREEN,
    title: "Configure Workflow Activities",
    label: "Activities Configuration",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_INFO_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_INFO_SCREEN,
    title: "Collect Sample Information",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NGS_RESULTS_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.NGS_RESULTS_SCREEN,
    title: "Saving of Results",
    label: "Analysis Results",
    description:
      "Outputs from the NGS workflow can be saved to the corresponding sample records in the Selector database. " +
      "For this, you must specify how the outputs from the workflow will be stored to the available sample attributes. " +
      "Workflow outputs that are not mapped to sample attributes will not be stored with the sample record in the database. ",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.REPORT_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.REPORT_SCREEN,
    title: "Define a Report Template",
    label: "Experiment Report",
    description: "Design the report that will be generated at the end of the analysis.",
  },
  [PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_STATUS_SCREEN]: {
    pageIdentifier: PLAYBOOK_SAMPLE_CONFIG_SCREEN_IDS.SAMPLE_STATUS_SCREEN,
    title: "Sample Status and Playbook Output",
    label: "Sample Status",
    description:
      "Every sample record has a status value. The status for each sample will be automatically updated during " +
      "playbook execution. Choose the status value for each of the situations below.",
  },
};

/// Action Types for Logging
export const LOG_ACTION = {
  AUTHENTICATION: 3,
  ADMIN_ACTIVITY: 4,
  ROUTE_CHANGE: 5,
  SEARCH: 6,
  SAVE: 7,
  DELETE: 8,
  MODIFY: 9,
  // DISPLAY: 10,
  // PRINT: 11,
  DOWNLOAD: 12,
  UPLOAD: 13,
  EXPORT: 14,
  // AUDIT_REPORT: 15,
  PROJECT: 16,
  PLAYBOOK: 17,
  // COMMENT: 18,
};

export enum CAPABILITY {
  CREATE_PROJECT = 1,
  DELETE_ANY_COMMENT = 2, // handled on server side
  ADMINISTRATION = 3,
  CREATE_AUDIT_REPORT = 4,
  DOWNLOAD_SYSTEM_LOGS = 17,
  USE_PRIVATE_WORKSPACE = 18, // handled on server side
  PRIVATE_SAVING = 19,
  PROJECT_COMMENTS = 20,
  DELETE_SAMPLES = 22,
  LAUNCH_ANALYST = 32,
  LAUNCH_PROCESSOR = 35,
  LAUNCH_ARRAY = 37,
  BLAST = 39,
  PHENOTYPE_EDITING = 40,
  SEQUENCE_UPLOAD = 42,
  SEQUENCE_DELETION = 43,
  PUBLISH_WORKFLOWS = 82,
  CREATE_VIRTUAL_GENOME = 83,
  LAUNCH_CLASSIC_PROCESSOR = 84,
  SAMPLE_LOCKING = 85,
  SAMPLE_UNLOCKING = 86,
  VIEW_PLAYBOOK_EXECUTION_LOGS = 87,
  REST_API_ACCESS = 100,
}

export const SEQUENCE_TYPE_LABEL = {
  4: "mRNA",
  5: "rRNA",
  6: "tRNA",
  12: "EST",
  15: "snoRNA",
  16: "snRNA",
  17: "lncRNA",
  18: "ncRNA",
};

export const ATTRIBUTE_ID = {
  SAMPLE_NAME: 1,
  SAMPLE_STATUS: 2,
  SAMPLE_DESCRIPTION: 3,
  SAMPLE_ADDED: 4,
  SAMPLE_MODIFIED: 5,
  SAMPLE_SUBMITTER: 6,
  SAMPLE_RECEIVER: 7,
  SAMPLE_TYPE: 8,
  SAMPLE_PROJECT: 9,
  SAMPLE_ORGANISM: 11,
};

export const REPRESENTER_COLUMN = {
  STATUS: "status",
  NAME: "name",
  DESCRIPTION: "description",
  ORGANISM: "organism",
};

export const DEFAULT_PROJECT_CAPABILITIES = [CAPABILITY.PRIVATE_SAVING, CAPABILITY.PROJECT_COMMENTS];

// Classic UI Analyzers
export const ANALYZERS: any = {
  LOCUS_ANALYZER: "Locus Analyzer",
  DNA_ANALYZER: "DNA Analyzer",
  PROTEIN_ANALYZER: "Protein Analyzer",
  SEQUENCE_ANALYZER: "Sequence Analyzer",
  PATHWAY_ANALYZER: "Pathway Analyzer",
};

export const WEB_ANALYZERS: any = {
  SEQUENCE_ANALYZER: "Sequence Viewer",
};

// We limit some kind of threshold for the number of samples to be edited at once
// This is anyway the limit for Oracle, since the editing endpoints (validation and editing) are not batched
export const SAMPLES_COUNT_EDITING_LIMIT = 1000;

export const CELL_EXPANSION = {
  NONE: "none",
  STANDALONE: "standalone",
  GROUP: "group",
  CONTEXT: "context",
};

export enum TableRestriction {
  GENOME = "GENOME",
  CONTIG = "CONTIG",
  REGION = "REGION",
  SELECTION = "SELECTION",
  DEFAULT = "REGION",
}

export const BEST_HIT_COLUMN_WIDTH = 200;

// TabbedTableView component does expect an analysis group, therefore we have to make
// a distinction between a genome (or gene or protein...) being not part of any analysis group
// and the information not being available yet.
export const emptyAnalysisGroup: VariantAnalysisGroup = {
  id: null,
  name: null,
  genomes: [],
};

export const NON_GENOMIC_GENOME_ID = 0;
export const PUBLIC_PROJECT_GROUP_ID = 0;

export const GFF_FEATURE_TYPE_GENE = "gene";
export const GFF_FEATURE_TYPE_MRNA = "mRNA";
export const GFF_FEATURE_TYPE_EXON = "exon";
export const GFF_FEATURE_TYPE_CDS = "CDS";
export const GFF_FEATURE_TYPE_TRNA = "tRNA";
export const GFF_FEATURE_TYPE_RRNA = "rRNA";
export const GFF_FEATURE_TYPE_SNORNA = "snoRNA";
export const GFF_FEATURE_TYPE_SNRNA = "snRNA";
export const GFF_FEATURE_TYPE_LNCRNA = "lncRNA";
export const GFF_FEATURE_TYPE_NCRNA = "ncRNA";
export const GFF_FEATURE_TYPE_PSEUDOGENE = "pseudogene";
export const GFF_FEATURE_TYPE_AA_FEATURE = "aaFeature";
export const GFF_FEATURE_TYPE_NU_FEATURE = "nuFeature";

export const GFF_FEATURE_TYPES: any = [
  GFF_FEATURE_TYPE_GENE,
  GFF_FEATURE_TYPE_MRNA,
  GFF_FEATURE_TYPE_EXON,
  GFF_FEATURE_TYPE_CDS,
  GFF_FEATURE_TYPE_TRNA,
  GFF_FEATURE_TYPE_RRNA,
  GFF_FEATURE_TYPE_LNCRNA,
  GFF_FEATURE_TYPE_NCRNA,
  GFF_FEATURE_TYPE_SNORNA,
  GFF_FEATURE_TYPE_SNRNA,
  GFF_FEATURE_TYPE_PSEUDOGENE,
];

export const GFF_TO_BROWSER_FEATURE_TYPE = {
  gene: ENTITY_TYPE.GENE,
  mRNA: ENTITY_TYPE.TRANSCRIPT_LOCATION,
  CDS: ENTITY_TYPE.PROTEIN_LOCATION,
};

export const GFF_TO_EXPORT_TRACK_TYPE = {
  gene: ENTITY_TYPE.GENE,
  mRNA: GFF_FEATURE_TYPE_MRNA,
  exon: GFF_FEATURE_TYPE_EXON,
  CDS: ENTITY_TYPE.PROTEIN,
  tRNA: GFF_FEATURE_TYPE_TRNA,
  rRNA: GFF_FEATURE_TYPE_RRNA,
  lncRNA: GFF_FEATURE_TYPE_LNCRNA,
  ncRNA: GFF_FEATURE_TYPE_NCRNA,
  snoRNA: GFF_FEATURE_TYPE_SNORNA,
  snRNA: GFF_FEATURE_TYPE_SNRNA,
  pseudogene: GFF_FEATURE_TYPE_PSEUDOGENE,
};

export const nonBreakingSpace = "\u00A0";

// Token
export const TOKEN_KEY = `genedata_auth_token${import.meta.env.DEV ? "" : `_${location.pathname.split("/")[1]}`}`;
export const CLASSIC_UI_TOKEN_KEY = "genedata_auth_token";
export const EXTERNAL_LOGIN_TOKEN = "genedata_external_token";
export const AUTH_KEY = "Authorization";
export const X_AUTH_KEY = "X-Authorization";

export const GETTING_STARTED_ELEMENT_ID = {
  CONTENT_BROWSER: "content-browser-link-id",
  PLAYBOOKS: "playbooks-link-id",
  GLOBAL_SEARCH: "global-search-field-id",
  CUSTOMIZE_DASHBOARD: "configure-dashboard-button-id",
  DOCUMENTATION: "documentation-link-id",
  HELP_MENU_BUTTON: "help-button-id",
};

export type SortingField = "created" | "modified" | "custom" | "owner" | "name" | "project.name";
export interface ISortInfo {
  label: string;
  field: SortingField;
  ascending: boolean;
}

// TODO: Imported from string to avoid cyclic deps issue
function capitalizeFirstLetter(label: string): string {
  return label && label.charAt(0).toUpperCase() + label.slice(1);
}

export const SORT_OPTIONS: Record<string, ISortInfo> = {
  CREATED: { label: "Newest", field: "created", ascending: false },
  DATE_MODIFIED: { label: "Date Modified", field: "modified", ascending: false },
  CUSTOM: { label: "Manually-defined", field: "custom", ascending: true },
  OWNER: { label: "Owner", field: "owner", ascending: true },
  NAME: { label: "Name", field: "name", ascending: true },
  PROJECT: { label: capitalizeFirstLetter(SYSTEM_PARAMS.projectLabel), field: "project.name", ascending: true }, // TODO: this line introduces a cyclic dependency with 'string'
};

// used in context of saved items, recent items, ...
export const ITEMS_TYPE = {
  GROUP: "group",
  FILE: FILE_TYPE,
  VIEW: ENTITY_TYPE.VIEW,
  SAMPLE_PARENT: ENTITY_TYPE.SAMPLE_PARENT,
  REGION_FLAG: ENTITY_TYPE.REGION_FLAG,
  SEARCH_RESULT: "searchResult",
  ANALYST_SESSION: ENTITY_TYPE.ANALYST_SESSION,
  EVENT: ENTITY_TYPE.EVENT,
  COORD_LIST: ENTITY_TYPE.COORD_LIST,
};

export const DEFAULT_MIN_COLUMN_WIDTH = 110;

export enum WORKFLOW_PARAM_USAGE {
  GENOME_INPUT = "GENOME_INPUT", // selection of genome folder or Explorer genome e.g. reference genome
  GENOMIC_ANNOTATION_INPUT = "GENOMIC_ANNOTATION_INPUT",
  UNALIGNED_READS_INPUT_FILE_PATHS = "UNALIGNED_READS_INPUT_FILE_PATHS", // multiple input files e.g. reads input files for NGS
  ALIGNED_READS_INPUT_FILE_PATHS = "ALIGNED_READS_INPUT_FILE_PATHS", // multiple input files e.g. reads input files for NGS
}

export enum PLAYBOOK_REPORT_TEMPLATE_RESOURCES {
  TEMPLATE_FILE = "FO",
  DESIGN_FILE = "XRE",
  SCHEMA_FILE = "XSD",
  DATA_EXAMPLE_FILE = "XML",
}

export enum PASSWORD_STATUS {
  EXPIRED = "EXPIRED",
  EXPIRED_AND_LOCKED = "EXPIRED_AND_LOCKED",
  EXPIRED_AND_LOCKED_TIMED = "EXPIRED_AND_LOCKED_TIMED",
  EXPIRED_GRACE = "EXPIRED_GRACE",
  EXPIRED_GRACE_AND_LOCKED = "EXPIRED_GRACE_AND_LOCKED",
  EXPIRED_GRACE_AND_LOCKED_TIMED = "EXPIRED_GRACE_AND_LOCKED_TIMED",
  LOCKED = "LOCKED",
  LOCKED_TIMED = "LOCKED_TIMED",
  OPEN = "OPEN",
  UNKNOWN = "UNKNOWN",
}

export enum FILE_BROWSE_TYPES {
  REGULAR_FILE = "REGULAR_FILE",
  PROJECT = "PROJECT",
  PROJECT_GROUP = "PROJECT_GROUP",
  DIRECTORY = "DIRECTORY",
  SYMLINK = "SYMLINK",
  UNDEFINED = "UNDEFINED",
  ROOT = "ROOT",
  HYPERLINK = "HYPERLINK",
}

export const FILE_DISPLAY_NAME = {
  [FILE_BROWSE_TYPES.REGULAR_FILE]: "file",
  [FILE_BROWSE_TYPES.DIRECTORY]: "folder",
  [FILE_BROWSE_TYPES.HYPERLINK]: "hyperlink",
  [FILE_BROWSE_TYPES.UNDEFINED]: "resource",
};

/**
 * Use file extension detection if possible
 * @see getFilenameInfo
 */
export enum CONTENT_TYPES {
  LINK = 18,
  DIRECTORY = 52,
}

export const DEFAULT_DATAVIEWER_SYMBOL_SIZE = 5;

export const TAG_COLOR = "#dddddd";

export enum PROJECT_ROLE {
  ADMIN = 1,
  USER = 2,
  MANAGER = 3,
}

// if there more than VCF_MUTANT_GENOMES_COUNT_THRESHOLD mutant genomes in the analysis group then we show none of them and expect the user to choose first
export const GENOME_VIEWER_MUTANT_GENOMES_COUNT_THRESHOLD = 20;

export const DEFAULT_COVERAGE_HISTOGRAM_COLOR = "#DDDDDD";
export const DEFAULT_BED_COLOR = "#fee08b";
export const DEFAULT_COORDINATE_COLOR = "#990000";

export enum NGS_READS_PILEUP_DENSITY {
  COMPACT = "COMPACT",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export enum NGS_READS_PILEUP_COLORING {
  DEFAULT = "DEFAULT",
  STRAND = "STRAND",
  PE_STRAND = "PE STRAND",
  MAPPING_QUALITY = "MAPPING QUALITY",
  SOFT_CLIPPING = "SOFT CLIPPING",
  SPLIT_READS = "SPLIT READS",
  READ_LENGTH = "READ LENGTH",
}

export enum ORG_PROPERTY_VALUE_TYPE {
  FREE = "F",
  NUMERIC = "N",
}

export const DEFAULT_ORG_PROPERTY_GROUP = 0;

export enum COLUMN_DYNAMIC_TYPES {
  ORGANISM_PROPERTY = "organismProperty",
  TRACKER_ATTRIBUTE = "attribute",
  CUSTOM_FILE_COLUMN = "customColumn", // e.g. for BED file grids
  CLASSIFICATION = "classification",
  EXTERNAL_REFERENCE = "externalReference",
}

export enum EVENT_RESOURCE_TYPES {
  ALIGNED_READS_FILE = "Aligned Reads File",
  DATASET = "Dataset",
  GENOME_ANNOTATION = "Genome Annotation",
  RAW_READS_FILE = "Raw Reads File",
  REFERENCE_GENOME = "Reference Genome",
  REPORT = "Report",
  UNKNOWN = "Unknown",
}

export enum PHENOTYPE_TYPE {
  ATTRIBUTE = "attribute",
  ORG_PROPERTY = "orgPropertyType",
}

export enum HTML_FILE_PREVIEW {
  STRICT = "strict",
  RELAXED = "relaxed",
}
export const uploaderFieldLabel = "Uploaded by";

export const ALERTS_QUEUE_CONTAINER_ID = "alerts-queue";
export const DISCONNECTION_QUEUE_CONTAINER_ID = "disconnected-errors-queue";

export const ANALYTICAL_DATA_VIEWER_MAXIMAL_LIGHTNESS = 0.93;
